import React, { useState } from 'react';
import { MessageCircle, X, Phone, Mail, Calendar } from 'lucide-react';
import { cn } from '../lib/utils';

const WHATSAPP_NUMBER = '525548564696';
const WHATSAPP_MESSAGE = '¡Hola! Me interesa obtener más información sobre los servicios audiovisuales de Viubux.';

export function FloatingContact() {
  const [isOpen, setIsOpen] = useState(false);

  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;

  return (
    <div className="fixed bottom-24 right-6 z-50" data-testid="floating-contact">
      {/* Expanded Menu */}
      <div
        className={cn(
          "absolute bottom-16 right-0 transition-all duration-300 transform origin-bottom-right",
          isOpen 
            ? "opacity-100 scale-100 translate-y-0" 
            : "opacity-0 scale-95 translate-y-4 pointer-events-none"
        )}
      >
        <div className="bg-[#0f133a] border border-white/10 rounded-2xl shadow-2xl shadow-black/50 p-4 w-72">
          {/* Header */}
          <div className="flex items-center gap-3 mb-4 pb-3 border-b border-white/10">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#aa2aff] to-[#7012b2] flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm">¿Necesitas ayuda?</p>
              <p className="text-slate-400 text-xs">Estamos para servirte</p>
            </div>
          </div>

          {/* Contact Options */}
          <div className="space-y-2">
            {/* WhatsApp - Main CTA */}
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 rounded-xl bg-[#25D366] hover:bg-[#20bd5a] transition-colors group"
              data-testid="whatsapp-link"
            >
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-white fill-current">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              <div className="flex-grow">
                <p className="text-white font-semibold text-sm">WhatsApp</p>
                <p className="text-white/80 text-xs">Respuesta inmediata</p>
              </div>
              <div className="w-2 h-2 rounded-full bg-white animate-pulse"></div>
            </a>

            {/* Email */}
            <a
              href="mailto:email@viubux.com"
              className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
              data-testid="email-link"
            >
              <div className="w-10 h-10 rounded-full bg-[#aa2aff]/20 flex items-center justify-center">
                <Mail className="w-5 h-5 text-[#aa2aff]" />
              </div>
              <div className="flex-grow">
                <p className="text-white font-medium text-sm">Email</p>
                <p className="text-slate-400 text-xs">email@viubux.com</p>
              </div>
            </a>

            {/* Phone */}
            <a
              href="tel:+525548564696"
              className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
              data-testid="phone-link"
            >
              <div className="w-10 h-10 rounded-full bg-[#aa2aff]/20 flex items-center justify-center">
                <Phone className="w-5 h-5 text-[#aa2aff]" />
              </div>
              <div className="flex-grow">
                <p className="text-white font-medium text-sm">Llamar</p>
                <p className="text-slate-400 text-xs">+52 55 4856 4696</p>
              </div>
            </a>

            {/* Schedule */}
            <a
              href="https://vbx.viubux.com/agenda"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
              data-testid="schedule-link"
            >
              <div className="w-10 h-10 rounded-full bg-[#aa2aff]/20 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-[#aa2aff]" />
              </div>
              <div className="flex-grow">
                <p className="text-white font-medium text-sm">Agendar Reunión</p>
                <p className="text-slate-400 text-xs">Elige fecha y hora</p>
              </div>
            </a>
          </div>

          {/* Footer */}
          <div className="mt-4 pt-3 border-t border-white/10 text-center">
            <p className="text-slate-500 text-xs">
              Horario: Lun - Vie 9:00 - 18:00
            </p>
          </div>
        </div>
      </div>

      {/* Main Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "w-14 h-14 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center",
          isOpen 
            ? "bg-[#0f133a] border border-white/20 rotate-0" 
            : "bg-[#25D366] hover:bg-[#20bd5a] hover:scale-110"
        )}
        data-testid="floating-contact-btn"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <svg viewBox="0 0 24 24" className="w-7 h-7 text-white fill-current">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        )}
      </button>

      {/* Pulse Animation (only when closed) */}
      {!isOpen && (
        <span className="absolute bottom-0 right-0 w-14 h-14 rounded-full bg-[#25D366] animate-ping opacity-30 pointer-events-none"></span>
      )}
    </div>
  );
}

export default FloatingContact;
